export type BrandKey =
  | "betarion"
  | "royalLounge"
  | "betarionStage"
  | "nextwin"
  | "rtp"
  | "rtpStage"
  | "planets"
  | "planetsStage"
  | "cazillaStage"
  | "here2winStage"
  | "fiveCasinoStage"
  | "cazilla"
  | "pumpkings"
  | "here2win"
  | "fiveCasino"
  | "joker";

export type BrandConfig = {
  title: string;
  description: string;
  logoUrl: string;
  logoMobUrl?: string;
  faviconUrl: string;
  apiBaseUrl: string;
  is2FA?: boolean;
  currencySymbol?: string;
};

const DEFAULT_CURRENCY_SYMBOL = "€";

const HOSTNAME_BRAND_MAP: Record<string, BrandKey> = {
  "backoffice-staging.betsystems.info": "nextwin",
  "backoffice.betarion.com": "betarion",
  "backoffice-staging.betarion.com": "betarionStage",
  "bo.royallounge.net": "royalLounge",
  "backoffice-staging.rtp.bet": "rtpStage",
  "bo.rtp.bet": "rtp",
  "backoffice-staging.pumpkings.com": "pumpkings",
  "backoffice-staging.planets.casino": "planetsStage",
  "bo.planets.casino": "planets",
  "backoffice-staging.cazilla.casino": "cazillaStage",
  "backoffice-staging.here2win.com": "here2winStage",
  "backoffice-staging.five.casino": "fiveCasinoStage",
  "bo.cazilla.casino": "cazilla",
  "bo.here2win.com": "here2win",
  "bo.five.casino": "fiveCasino",
  "joker-bo.kingsizer.solutions": "joker",
};

export const detectBrandKey = (hostname = window.location.hostname): BrandKey => {
  return HOSTNAME_BRAND_MAP[hostname] ?? "nextwin";
};

export const BRAND_CONFIGS: Record<BrandKey, BrandConfig> = {
  betarion: {
    title: "Betarion Backoffice",
    description: "Betarion BackOffice",
    logoUrl: "https://cdn.betsystems.info/logo/betarion.webp",
    logoMobUrl: "https://cdn.betarion.com/logo/betarion-mini.webp",
    faviconUrl: "https://cdn.betarion.com/logo/betarion-favicon.ico",
    apiBaseUrl: "api.betarion.com",
    is2FA: true,
  },
  betarionStage: {
    title: "Betarion Backoffice | Stage",
    description: "Betarion Stage",
    logoUrl: "https://cdn.betsystems.info/logo/betarion.webp",
    logoMobUrl: "https://cdn.betarion.com/logo/betarion-mini.webp",
    faviconUrl: "https://cdn.betarion.com/logo/betarion-favicon.ico",
    apiBaseUrl: "api-staging.betarion.com",
    is2FA: true,
  },
  royalLounge: {
    title: "Royal Lounge Backoffice",
    description: "Royal Lounge BackOffice",
    logoUrl: "https://cdn.royallounge.net/logo/royallounge.webp",
    logoMobUrl: "https://cdn.royallounge.net/logo/royal-lounge-mini.webp",
    faviconUrl: "https://cdn.royallounge.net/logo/royal-lounge-favicon.ico",
    apiBaseUrl: "api.royallounge.net",
    is2FA: false,
  },
  nextwin: {
    title: "NextWin BackOffice",
    description: "NextWin BackOffice",
    logoUrl: "https://cdn.betsystems.info/logo/nextwin-logo.webp",
    logoMobUrl: "https://cdn.betsystems.info/logo/nextwin-logo-rb-mini.webp",
    faviconUrl: "https://cdn.betsystems.info/logo/nextwin-favicon.ico",
    apiBaseUrl: "api-staging.betsystems.info",
    is2FA: true,
  },
  rtp: {
    title: "RTP BackOffice",
    description: "RTP BackOffice",
    logoUrl: "https://cdn.rtp.bet/rtp.bet/logo/rtpbet-logo.webp",
    logoMobUrl: "https://cdn.rtp.bet/rtp.bet/logo/rtpbet-logo-mini.webp",
    faviconUrl: "https://cdn.rtp.bet/rtp.bet/logo/rtpbet-logo-mini.webp",
    apiBaseUrl: "api.rtp.bet",
    is2FA: true,
  },
  rtpStage: {
    title: "RTP BackOffice | Stage",
    description: "RTP BackOffice | Stage",
    logoUrl: "https://cdn.betsystems.info/logo/rtpbet-logo.webp",
    logoMobUrl: "https://cdn.betsystems.info/logo/rtpbet-logo-mini.webp",
    faviconUrl: "https://cdn.betsystems.info/logo/rtpbet-logo-mini.webp",
    apiBaseUrl: "api-staging.rtp.bet",
    is2FA: true,
  },
  pumpkings: {
    title: "PumpKings BackOffice",
    description: "PumpKings BackOffice",
    logoUrl: "https://cdn.betsystems.info/logo/pumpkings-logo.webp",
    logoMobUrl: "https://cdn.betsystems.info/logo/pumpkings-logo-mini.webp",
    faviconUrl: "https://cdn.betsystems.info/logo/pumpkings-logo-mini.webp",
    apiBaseUrl: "api-staging.pumpkings.com",
    is2FA: true,
  },
  planets: {
    title: "PlanetsCasino BackOffice",
    description: "PlanetsCasino BackOffice",
    logoUrl: "https://cdn.planets.casino/planets/logo/planets-casino-logo.webp",
    logoMobUrl:
      "https://cdn.planets.casino/planets/logo/planets-casino-logo-mini.webp",
    faviconUrl:
      "https://cdn.planets.casino/planets/logo/planets-casino-logo-mini.webp",
    apiBaseUrl: "api.planets.casino",
    is2FA: true,
    currencySymbol: "$",
  },
  planetsStage: {
    title: "PlanetsCasino BackOffice | Stage",
    description: "PlanetsCasino BackOffice | Stage",
    logoUrl: "https://cdn.betsystems.info/logo/planets-logo.webp",
    logoMobUrl: "https://cdn.betsystems.info/logo/planets-logo-mini.webp",
    faviconUrl: "https://cdn.betsystems.info/logo/planets-logo-mini.webp",
    apiBaseUrl: "api-staging.planets.casino",
    is2FA: true,
    currencySymbol: "$",
  },
  cazillaStage: {
    title: "Cazilla BackOffice | Stage",
    description: "Cazilla BackOffice | Stage",
    logoUrl: "https://cdn.betsystems.info/logo/cazilla-logo.webp",
    logoMobUrl: "https://cdn.betsystems.info/logo/cazilla-logo-mini.webp",
    faviconUrl: "https://cdn.betsystems.info/logo/cazilla-logo-mini.webp",
    apiBaseUrl: "api-staging.cazilla.casino",
    is2FA: true,
  },
  here2winStage: {
    title: "Here2Win BackOffice | Stage",
    description: "Here2Win BackOffice | Stage",
    logoUrl: "https://cdn.betsystems.info/logo/here2win-logo.webp",
    logoMobUrl: "https://cdn.betsystems.info/logo/here2win-logo-mini.webp",
    faviconUrl: "https://cdn.betsystems.info/logo/here2win-logo-mini.webp",
    apiBaseUrl: "api-staging.here2win.com",
    is2FA: true,
  },
  fiveCasinoStage: {
    title: "Five Casino BackOffice | Stage",
    description: "Five Casino BackOffice | Stage",
    logoUrl: "https://cdn.betsystems.info/logo/five-casino-logo.svg",
    logoMobUrl: "https://cdn.betsystems.info/logo/five-casino-logo-mini.svg",
    faviconUrl: "https://cdn.betsystems.info/logo/five-casino-logo-mini.svg",
    apiBaseUrl: "api-staging.five.casino",
    is2FA: true,
  },
  cazilla: {
    title: "Cazilla BackOffice",
    description: "Cazilla BackOffice",
    logoUrl: "https://cdn.betsystems.info/logo/cazilla-logo.webp",
    logoMobUrl: "https://cdn.betsystems.info/logo/cazilla-logo-mini.webp",
    faviconUrl: "https://cdn.betsystems.info/logo/cazilla-logo-mini.webp",
    apiBaseUrl: "api.cazilla.casino",
    is2FA: true,
  },
  here2win: {
    title: "Here2Win BackOffice",
    description: "Here2Win BackOffice",
    logoUrl: "https://cdn.here2win.com/here2win/logo/here2win-logo.svg",
    logoMobUrl: "https://cdn.here2win.com/here2win/logo/here2win-logo-mini.svg",
    faviconUrl: "https://cdn.here2win.com/here2win/logo/here2win-logo-mini.svg",
    apiBaseUrl: "api.here2win.com",
    is2FA: true,
  },
  fiveCasino: {
    title: "Five Casino BackOffice",
    description: "Five Casino BackOffice",
    logoUrl: "https://cdn.betsystems.info/logo/five-casino-logo.svg",
    logoMobUrl: "https://cdn.betsystems.info/logo/five-casino-logo-mini.svg",
    faviconUrl: "https://cdn.betsystems.info/logo/five-casino-logo-mini.svg",
    apiBaseUrl: "api.five.casino",
    is2FA: true,
  },
  joker: {
    title: "Joker BackOffice",
    description: "Joker BackOffice",
    logoUrl: "https://cdn.betsystems.info/logo/nextwin-logo.webp",
    logoMobUrl: "https://cdn.betsystems.info/logo/nextwin-logo-rb-mini.webp",
    faviconUrl: "https://cdn.betsystems.info/logo/nextwin-favicon.ico",
    apiBaseUrl: "api-joker.kingsizer.solutions",
    is2FA: true,
  },
};

export const currentBrand = BRAND_CONFIGS[detectBrandKey()];
export const currencySymbol = currentBrand.currencySymbol ?? DEFAULT_CURRENCY_SYMBOL;
